/**
 * Image processing utilities for gallery system
 * Handles compression and resizing for preview images
 */

export interface ProcessImageOptions {
  maxWidth: number;
  maxHeight: number;
  quality: number; // 0.1 to 1.0
  format: 'jpeg' | 'webp' | 'png';
}

export const DEFAULT_PREVIEW_OPTIONS: ProcessImageOptions = {
  maxWidth: 800,
  maxHeight: 600,
  quality: 0.7, // 70% quality for good balance
  format: 'jpeg'
};

/**
 * Compress and resize image for preview use
 */
export const processImageForPreview = async (
  file: File,
  options: ProcessImageOptions = DEFAULT_PREVIEW_OPTIONS
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas not supported'));
      return;
    }

    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      const { width: newWidth, height: newHeight } = calculateDimensions(
        img.width,
        img.height,
        options.maxWidth,
        options.maxHeight
      );

      // Set canvas size
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Draw and compress image
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Convert to blob with compression
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to process image'));
            return;
          }

          // Create new file with preview suffix
          const originalName = file.name.split('.')[0];
          const newFileName = `${originalName}_preview.${options.format}`;
          const processedFile = new File([blob], newFileName, {
            type: `image/${options.format}`,
            lastModified: Date.now()
          });

          console.log(`Image processed: ${file.name} (${formatBytes(file.size)}) → ${newFileName} (${formatBytes(processedFile.size)})`);
          resolve(processedFile);
        },
        `image/${options.format}`,
        options.quality
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Load the image
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Calculate new dimensions maintaining aspect ratio
 */
function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  let { width, height } = { width: originalWidth, height: originalHeight };

  // Scale down if image is larger than max dimensions
  if (width > maxWidth) {
    height = (height * maxWidth) / width;
    width = maxWidth;
  }

  if (height > maxHeight) {
    width = (width * maxHeight) / height;
    height = maxHeight;
  }

  return {
    width: Math.round(width),
    height: Math.round(height)
  };
}

/**
 * Format bytes to human readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Check if file is an image
 */
export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/');
};

/**
 * Get optimal preview processing options based on file size
 */
export const getOptimalPreviewOptions = (fileSize: number): ProcessImageOptions => {
  // For very large files, use more aggressive compression
  if (fileSize > 10 * 1024 * 1024) { // > 10MB
    return {
      maxWidth: 600,
      maxHeight: 450,
      quality: 0.6,
      format: 'jpeg'
    };
  }

  // For medium files, use balanced settings
  if (fileSize > 2 * 1024 * 1024) { // > 2MB
    return {
      maxWidth: 700,
      maxHeight: 525,
      quality: 0.65,
      format: 'jpeg'
    };
  }

  // For smaller files, use default settings
  return DEFAULT_PREVIEW_OPTIONS;
};